// رحلة التعلّم — الجدارة ← المسار ← الدورات/التحديات ← التقدّم
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GlassCard, Chip, Skeleton, Bar, Ring } from "@/v2/components/ui";
import {
  Lock, Check, Crown, Clock, Star, Sparkles, Target, Rocket, Wrench, HeartHandshake,
  ChevronLeft, Layers, PlayCircle,
} from "lucide-react";

const ICONS = { Target, Wrench, HeartHandshake, Crown, Sparkles, Layers };
const compIcon = (n) => ICONS[n] || Target;

const STATE = {
  completed: { label: "مكتمل", tone: "emerald" },
  in_progress: { label: "قيد التنفيذ", tone: "gold" },
  available: { label: "متاح الآن", tone: "gold" },
  locked: { label: "مقفل", tone: "muted" },
  locked_admin: { label: "مقفل من الإدارة", tone: "muted" },
  suspended: { label: "متوقف مؤقتًا", tone: "red" },
  scheduled: { label: "قادم قريبًا", tone: "gold" },
};
const lockNote = (c) =>
  c.status === "locked" ? `أكمل «${c.locked_by || "المحطة السابقة"}» لفتح هذه المحطة`
    : c.status === "locked_admin" ? "هذه المحطة مقفلة حاليًا من الإدارة"
      : c.status === "suspended" ? "هذا المحتوى متوقف مؤقتًا من الإدارة"
        : c.status === "scheduled" ? "سيُفتح هذا التحدي في موعده المحدد" : "";

const Node = ({ c, index, isLast, onOpen }) => {
  const locked = ["locked", "locked_admin", "scheduled", "suspended"].includes(c.status);
  const active = c.status === "available" || c.status === "in_progress";
  const st = STATE[c.status] || STATE.locked;
  return (
    <div className="flex gap-3 sm:gap-4" data-testid={`journey-node-${c.id}`}>
      <div className="relative flex flex-col items-center w-10 sm:w-12 shrink-0">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-extrabold border-2 z-10 text-sm ${
          c.status === "completed" ? "bg-[#91cab8] text-[#25343f] border-[#91cab8]"
            : active ? "bg-gradient-to-br from-[#e3c799] to-[#bb9661] text-[#25343f] border-[#e3c799] v2-pulse"
              : "bg-white/5 text-[#bcc8cf] border-white/10"}`}>
          {c.status === "completed" ? <Check size={19} /> : locked ? <Lock size={15} /> : index + 1}
        </div>
        {!isLast && <div className="flex-1 w-[3px] rounded-full my-1"
          style={{ background: c.status === "completed" ? "linear-gradient(180deg,#91cab8,rgba(145,202,184,.25))" : "rgba(255,255,255,0.08)" }} />}
      </div>

      <div className={`flex-1 mb-3 ${active ? "" : ""}`}>
        {active ? (
          <GlassCard gold hover onClick={() => onOpen(c)} data-testid={`journey-current-${c.id}`}
            className="p-4 sm:p-5 cursor-pointer">
            <div className="flex items-start gap-4">
              <img src={c.thumbnail} alt="" className="hidden sm:block w-20 h-20 rounded-2xl object-cover shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <Chip tone="gold"><Rocket size={12} /> {c.status === "in_progress" ? "أكمل الآن" : "خطوتك الحالية"}</Chip>
                  <span className="text-[11px] text-[#bcc8cf]">المحطة {index + 1}</span>
                </div>
                <h4 className="font-extrabold text-lg mb-1 truncate">{c.title}</h4>
                <p className="text-sm text-[#bcc8cf] line-clamp-2 mb-3">{c.description}</p>
                <div className="flex items-center gap-3 text-xs text-[#bcc8cf] flex-wrap">
                  <span className="flex items-center gap-1"><Clock size={13} /> {c.video_duration}</span>
                  <span className="flex items-center gap-1"><Star size={13} className="text-[#e3c799]" /> {c.points} نقطة</span>
                  <span className="flex items-center gap-1 v2-emerald-text font-bold"><PlayCircle size={14} /> ابدأ المحطة</span>
                </div>
              </div>
            </div>
          </GlassCard>
        ) : (
          <button onClick={() => !locked && onOpen(c)} disabled={locked} data-testid={`journey-row-${c.id}`}
            className={`w-full text-start v2-inset px-4 py-3 flex items-center gap-3 transition-colors ${
              locked ? "opacity-60 cursor-not-allowed" : "hover:border-[#bb9661]/40"}`}>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm truncate">{c.title}</div>
              <div className="text-[11px] text-[#bcc8cf] truncate">
                {locked ? lockNote(c) : `${c.points} نقطة · ${c.video_duration}`}
              </div>
            </div>
            <Chip tone={st.tone}>{st.label}</Chip>
            {!locked && <ChevronLeft size={16} className="text-[#bcc8cf]" />}
          </button>
        )}
      </div>
    </div>
  );
};

export default function Journey() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["journey", user?.id],
    queryFn: async () => (await api.get("/journey")).data,
    enabled: Boolean(user?.id),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchInterval: 15000,
  });
  const open = (c) => navigate(`/journey/${c.id}`);

  if (isLoading) return <div className="space-y-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}</div>;

  const t = data?.totals || { total: 0, completed: 0, progress: 0 };
  const next = data?.next_step;

  return (
    <div className="space-y-7" data-testid="journey-page">
      <GlassCard gold className="p-6 sm:p-8 v2-grid v2-pattern relative overflow-hidden">
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-7">
          <Ring value={t.progress} size={128} stroke={11} id="journeyring">
            <div className="text-center">
              <div className="text-2xl font-extrabold">{t.progress}%</div>
              <div className="text-[10px] text-[#bcc8cf]">تقدّمك</div>
            </div>
          </Ring>
          <div className="flex-1 text-center sm:text-start">
            <div className="flex items-center gap-2 justify-center sm:justify-start mb-2">
              <Chip tone="emerald"><Sparkles size={12} /> {data?.employee_type_label || "مسارك"}</Chip>
              <Chip tone="muted">{t.completed} من {t.total} محطة</Chip>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold mb-2">رحلة التعلّم الخاصة بك</h1>
            {next ? (
              <p className="text-[#bcc8cf] mb-4">
                خطوتك التالية: <span className="text-white font-bold">{next.title}</span>
                <span className="block text-xs mt-1">{next.competency} · {next.path}</span>
              </p>
            ) : (
              <p className="text-[#bcc8cf] mb-4">🎉 أكملت جميع المحطات المتاحة في مساراتك — تابع لوحة المتصدرين.</p>
            )}
            {next && (
              <button onClick={() => navigate(`/journey/${next.challenge_id}`)} data-testid="journey-continue" className="v2-btn">
                <Rocket size={17} /> أكمل من حيث توقفت
              </button>
            )}
          </div>
        </div>
      </GlassCard>

      {(data?.competencies || []).length === 0 && (
        <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm" data-testid="journey-empty">
          لم يتم تخصيص محتوى لمسارك بعد — تواصل مع إدارة المنصة.
        </GlassCard>
      )}

      {(data?.competencies || []).map((comp, ci) => {
        const I = compIcon(comp.icon);
        return (
          <motion.section key={comp.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(ci * 0.08, 0.3) }} data-testid={`journey-comp-${comp.id}`} className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#91cab8]/15 text-[#91cab8] flex items-center justify-center shrink-0">
                <I size={24} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg md:text-lg font-extrabold">{comp.name}</h2>
                  <Chip tone="muted">{comp.progress}%</Chip>
                </div>
                <p className="text-xs text-[#bcc8cf] truncate">{comp.desc}</p>
              </div>
            </div>

            {comp.paths.map((p) => (
              <GlassCard key={p.id} className="p-5" data-testid={`journey-path-${p.id}`}>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Layers size={17} className="text-[#e3c799]" />
                  <h3 className="font-extrabold">{p.name}</h3>
                  <Chip tone={p.progress === 100 ? "emerald" : "gold"}>{p.completed}/{p.total} مكتمل</Chip>
                  <div className="w-full mt-1"><Bar value={p.progress} /></div>
                  {p.desc && <p className="text-xs text-[#bcc8cf] w-full">{p.desc}</p>}
                </div>
                <div>
                  {p.challenges.length === 0 && (
                    <div className="v2-inset p-4 text-center text-sm text-[#bcc8cf]" data-testid={`journey-path-empty-${p.id}`}>
                      تم تخصيص هذا المسار لك، وستُضاف محطاته التدريبية قريبًا.
                    </div>
                  )}
                  {p.challenges.map((c, i) => (
                    <Node key={c.id} c={c} index={i} isLast={i === p.challenges.length - 1} onOpen={open} />
                  ))}
                </div>
              </GlassCard>
            ))}
            {comp.paths.length === 0 && (
              <GlassCard className="p-5 text-sm text-[#bcc8cf]" data-testid={`journey-comp-empty-${comp.id}`}>
                تم تخصيص هذه الجدارة لك، ولا توجد مسارات منشورة بداخلها بعد.
              </GlassCard>
            )}
          </motion.section>
        );
      })}
    </div>
  );
}
